import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { NxNativeDateAdapter } from '@aposin/ng-aquila/datefield';

/**
 * Datepicker adapter that parses and formats dates as dd/mm/yyyy
 */
@Injectable()
export class CustomDateAdapter extends NxNativeDateAdapter {
  override parse(value: string | Date | null): Date | null {
    if (typeof value === 'string' && value.indexOf('/') > -1) {
      const parts = value.split('/');
      if (parts.length === 3) {
        const day = Number(parts[0]);
        const month = Number(parts[1]) - 1;
        const year = Number(parts[2]);
        return new Date(year, month, day);
      }
    }
    return super.parse(value);
  }

  override format(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
}

/**
 * Validates time input in HH:mm format (24-hour format)
 * @params value - The time value to validate
 * @return ValidationErrors or null
 */
export function timeValidator(value: string | null): ValidationErrors | null {
  if (!value) return null;

  const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
  
  if (!timeRegex.test(value)) {
    return { invalidTime: true };
  }

  return null;
}

const PARIS_TIME_ZONE = 'Europe/Paris';

interface DateTimeParts {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Wall-clock date and time of an instant in Paris time, whatever the browser timezone is
 * @params date - The instant
 * @return The Paris date and time parts (month is 1-12)
 */
function getParisDateTimeParts(date: Date): DateTimeParts {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: PARIS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(p => p.type === type)?.value);

  return {
    year: part('year'),
    month: part('month'),
    day: part('day'),
    hours: part('hour'),
    minutes: part('minute'),
    seconds: part('second')
  };
}

/**
 * Converts a Paris wall-clock date and time to the matching UTC instant (DST aware)
 */
function parisWallClockToUtc(year: number, monthIndex: number, day: number, hours: number, minutes: number): Date {
  const wallClockAsUtc = Date.UTC(year, monthIndex, day, hours, minutes);
  let utc = wallClockAsUtc;
  // Two passes so the offset is the one in force at the resulting instant (daylight saving changes)
  for (let pass = 0; pass < 2; pass++) {
    const paris = getParisDateTimeParts(new Date(utc));
    const parisAsUtc = Date.UTC(paris.year, paris.month - 1, paris.day, paris.hours, paris.minutes, paris.seconds);
    utc = wallClockAsUtc - (parisAsUtc - utc);
  }
  return new Date(utc);
}

/**
 * Used when submitting forms with separate date and time fields.
 * The date and time entered are Paris time, the result is the UTC instant.
 * @params date - The date value (only its calendar day is used when a time is given)
 * @params time - The time value, HH:mm Paris time
 * @return UTC ISO date-time string (e.g. 2026-10-19T11:00:00Z for 19/10/2026 13:00 Paris time) or undefined
 */
export function combineDateTime(date: Date | null, time: string | null): string | undefined {
  if (!date) return undefined;

  const [hours, minutes] = time
    ? time.split(':').map(Number)
    : [date.getHours(), date.getMinutes()];

  const utcDate = parisWallClockToUtc(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes);

  return utcDate.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * Splits a UTC date-time string into the Paris date and time shown in the form fields
 * @params dateString - UTC ISO date-time string
 * @return The calendar day for the datepicker and the HH:mm Paris time, or nulls when invalid
 */
export function toParisDateAndTime(dateString: string | undefined): { date: Date | null; time: string | null } {
  const instant = new Date(dateString ?? '');
  if (!dateString || Number.isNaN(instant.getTime())) return { date: null, time: null };

  const paris = getParisDateTimeParts(instant);
  return {
    date: new Date(paris.year, paris.month - 1, paris.day),
    time: `${String(paris.hours).padStart(2, '0')}:${String(paris.minutes).padStart(2, '0')}`
  };
}

/**
 * To prevent invalid characters in date input fields
 * @params event - Keyboard event
 * @return
 */
export function onDateKeydown(event: KeyboardEvent): void {
  const key = event.key;
  
  if (event.ctrlKey || event.metaKey || 
      ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(key)) {
    return;
  }
  
  if (!/^[0-9/]$/.test(key)) {
    event.preventDefault();
  }
}

/**
 * Restricts input to valid time values (hours 00-23, minutes 00-59)
 * @params event - Input event
 * @params timeControl - Optional time control object
 * @return
 */
export function onTimeInput(event: Event, timeControl?: { markAsTouched: () => void; updateValueAndValidity: () => void }): void {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/[^0-9:]/g, '');
  
  const isMinutesInput = input.classList.contains('nx-timefield__input--minutes') || input.getAttribute('aria-label')?.toLowerCase().includes('minute');
  
  value = value.substring(0, 2);
  
  if (value) {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      if (isMinutesInput) {
        if (numValue > 59) {
          value = '59';
        }
      } else {
        if (numValue > 23) {
          value = '23';
        }
      }
    }
  }
  
  if (input.value !== value) {
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    if (timeControl) {
      timeControl.markAsTouched();
      timeControl.updateValueAndValidity();
    }
  }
}

/**
 * Formats a UTC date-time string to "DD/MM/YYYY at HH.mm am|pm, Paris time", whatever the browser timezone is
 * @params dateString - The date string to format
 * @return Formatted date-time string, or the input when it is not a valid date
 */
export function formatDateTime(dateString: string): string {
  const instant = new Date(dateString);
  if (Number.isNaN(instant.getTime())) return dateString;

  const paris = getParisDateTimeParts(instant);
  const day = String(paris.day).padStart(2, '0');
  const month = String(paris.month).padStart(2, '0');
  const minutes = String(paris.minutes).padStart(2, '0');
  const ampm = paris.hours >= 12 ? 'pm' : 'am';
  const displayHours = String(paris.hours % 12 || 12).padStart(2, '0');

  return `${day}/${month}/${paris.year} at ${displayHours}.${minutes} ${ampm}, Paris time`;
}
